import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { format } from "date-fns";

import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { ViewAction } from "@/components/table/actions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, type ColumnDef } from "@/components/table";
import { PageDataStats } from "@/components/PageDataStats";
import { ROUTES } from "@/constants/routes";
import { useMsDisputePageParams } from "../params";
import { cn } from "@/utils/cn";
import {
  useListMyDisputesQuery,
  useGetMyDisputeStatsQuery,
  type DisputeStatus,
  type IDispute,
} from "@/services/(company)/payrollDisputesApi";

const STATUS_ALL = "all" as const;
const buildStatusOptions = (
  translate: (key: string) => string,
): {
  value: typeof STATUS_ALL | DisputeStatus;
  label: string;
}[] => [
  { value: STATUS_ALL, label: translate("ui.all_statuses_x") },
  { value: "PENDING", label: translate("ui.pending") },
  { value: "RESOLVED", label: translate("ui.resolved") },
  { value: "REJECTED", label: translate("labels.rejected") },
];

const STATUS_TONE: Record<DisputeStatus, string> = {
  PENDING: "bg-yellow-light-3 text-yellow-700",
  UNDER_REVIEW: "bg-blue-50 text-blue-700",
  RESOLVED: "bg-success-100/10 text-success-100",
  REJECTED: "bg-red-50 text-red-600",
};

const disputeStatusLabel = (
  status: DisputeStatus,
  translate: (key: string) => string,
) => {
  const KEY: Record<DisputeStatus, string> = {
    PENDING: "ui.pending",
    UNDER_REVIEW: "company:payrolls.under_review",
    RESOLVED: "ui.resolved",
    REJECTED: "labels.rejected",
  };
  return translate(KEY[status]);
};

export const MySpaceDisputes = () => {
  const { t: translate } = useTranslation("my-space");
  usePageTitle(withBranding(translate("page_titles.my_disputes")));
  const navigate = useNavigate();
  // Status and page live in the URL — see useTableParams.
  const tp = useMsDisputePageParams();
  const status = tp.filters.status as typeof STATUS_ALL | DisputeStatus;

  const { data, isFetching } = useListMyDisputesQuery({
    page: tp.page,
    limit: 20,
    ...(status !== STATUS_ALL ? { status } : {}),
  });

  const disputes = useMemo(() => data?.data ?? [], [data]);

  const { data: statsRes, isLoading: statsLoading } =
    useGetMyDisputeStatsQuery();
  const stats = statsRes?.data;
  // Each tile is the status filter it counts; the total is "no filter".
  const statCards = [
    {
      name: translate("ui.total_disputes"),
      value: stats?.total ?? 0,
      icon: "/icons/interface-icons/stats/payroll/icon-3d-coin-pot.png",
      ...tp.statFilter({ status: STATUS_ALL }),
    },
    {
      name: translate("ui.pending"),
      value: stats?.pending ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-clock-gold.png",
      ...tp.statFilter({ status: "PENDING" }),
    },
    {
      name: translate("ui.resolved"),
      value: stats?.resolved ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-green-check.png",
      ...tp.statFilter({ status: "RESOLVED" }),
    },
    {
      name: translate("labels.rejected"),
      value: stats?.rejected ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-red-x.png",
      ...tp.statFilter({ status: "REJECTED" }),
    },
  ];

  const columns: ColumnDef<IDispute>[] = [
    {
      key: "period",
      label: translate("labels.period"),
      className: "w-[16%]",
      render: (r) => (
        <span className="text-sm font-medium text-gray-900">
          {r.payrollRun?.periodStart
            ? format(new Date(r.payrollRun.periodStart), "MMM yyyy")
            : "—"}
        </span>
      ),
    },
    {
      key: "category",
      label: translate("shared.category"),
      className: "w-[18%]",
      render: (r) => (
        <span className="text-sm text-gray-700 capitalize">
          {translate(
            `company:payrolls.dispute_category_${r.category.toLowerCase()}`,
          )}
        </span>
      ),
    },
    {
      key: "lines",
      label: translate("ui.lines"),
      className: "w-[10%]",
      render: (r) => (
        <span className="text-sm text-gray-700">{r.lines.length}</span>
      ),
    },
    {
      key: "diff",
      label: translate("ui.net_diff"),
      className: "w-[14%]",
      render: (r) => (
        <span
          className={cn(
            "text-sm font-semibold",
            r.totalProposedDiff > 0
              ? "text-success-100"
              : r.totalProposedDiff < 0
                ? "text-red-500"
                : "text-gray-700",
          )}
        >
          {r.totalProposedDiff > 0 ? "+" : ""}
          {r.totalProposedDiff.toFixed(2)}
        </span>
      ),
    },
    {
      key: "status",
      label: translate("labels.status"),
      className: "w-[14%]",
      render: (r) => (
        <span
          className={cn(
            "text-[11px] font-semibold px-2 py-0.5 rounded-xs",
            STATUS_TONE[r.status],
          )}
        >
          {disputeStatusLabel(r.status, translate)}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: translate("ui.raised_on"),
      className: "w-[14%]",
      render: (r) => (
        <span className="text-xs text-gray-500">
          {format(new Date(r.createdAt), "dd MMM yyyy")}
        </span>
      ),
    },
    {
      key: "action",
      label: translate("labels.actions"),
      className: "w-[10%]",
      render: (r) => (
        <ViewAction
          tooltip={translate("labels.view_dispute")}
          onClick={() => navigate(ROUTES.HOME.PAYROLL.DISPUTE_DETAIL(r.id))}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageDataStats data={statCards} loading={statsLoading} />
      <div className="rounded-xs border border-borderSecondary bg-white flex flex-col gap-4 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.HOME.PAYROLL.ROOT)}
              className="border border-card-background rounded-xs p-2 w-fit h-fit shrink-0"
            >
              <ChevronLeft className="text-primary size-5 rtl-flip" />
            </button>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-gray-900">
                {translate("ui.my_disputes_x")}
              </span>
              <span className="text-xs text-gray-400">
                {translate("ui.track_your_raised_payroll_disputes")}
              </span>
            </div>
          </div>
          <Select
            value={status}
            onValueChange={(v) => tp.setFilter("status", v)}
          >
            <SelectTrigger className="h-9 w-44 bg-white text-xs font-medium">
              <SelectValue placeholder={translate("labels.status")} />
            </SelectTrigger>
            <SelectContent>
              {buildStatusOptions(translate).map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Table<IDispute>
          noPadding
          columns={columns}
          data={disputes}
          loading={isFetching}
          emptyTitle={translate("ui.no_disputes_raised")}
          emptyMessage={translate("ui.disputes_you_raise_will_appear_here")}
          pagination={{
            currentPage: tp.page,
            totalPages: data?.meta?.totalPages ?? 1,
            onPageChange: tp.setPage,
          }}
        />
      </div>
    </div>
  );
};
